import moment from 'moment';

import {
  DATE_RELATIONS,
  DateFilterName,
  TransformedDateFilter,
} from '@frontend/api';

import { TimeUnitSingular } from 'components/listTablePage';

export type DateFilter<RELATION extends DATE_RELATIONS> =
  RELATION extends DATE_RELATIONS.ON
    ? {
        relation: RELATION;
        startDate: moment.Moment;
      }
    : RELATION extends DATE_RELATIONS.BEFORE | DATE_RELATIONS.AFTER
    ? {
        relation: RELATION;
        startDate: moment.Moment;
        relativeDate: string;
      }
    : RELATION extends DATE_RELATIONS.BETWEEN
    ? {
        relation: RELATION;
        startDate: moment.Moment;
        endDate: moment.Moment;
      }
    : RELATION extends
        | DATE_RELATIONS.CURRENT
        | DATE_RELATIONS.IN_THIS_HOUR
        | DATE_RELATIONS.TODAY
        | DATE_RELATIONS.IN_THIS_WEEK
        | DATE_RELATIONS.IN_THIS_MONTH
        | DATE_RELATIONS.IN_THIS_YEAR
        | DATE_RELATIONS.NEVER
    ? {
        relation: RELATION;
        unit: string;
      }
    : RELATION extends
        | DATE_RELATIONS.PREVIOUS
        | DATE_RELATIONS.NEXT
        | DATE_RELATIONS.IN_THE_LAST
        | DATE_RELATIONS.IN_THE_NEXT
    ? {
        relation: RELATION;
        unit: string;
        amount: string;
        isInclusive?: boolean;
      }
    : never;

const relationSplitChar = '_' as const;
const rangeSplitChar = ',' as const;

export const formatRelativeDateFilter = (
  filter: DateFilter<
    | DATE_RELATIONS.CURRENT
    | DATE_RELATIONS.IN_THIS_HOUR
    | DATE_RELATIONS.TODAY
    | DATE_RELATIONS.IN_THIS_WEEK
    | DATE_RELATIONS.IN_THIS_MONTH
    | DATE_RELATIONS.IN_THIS_YEAR
    | DATE_RELATIONS.NEVER
    | DATE_RELATIONS.PREVIOUS
    | DATE_RELATIONS.NEXT
    | DATE_RELATIONS.IN_THE_LAST
    | DATE_RELATIONS.IN_THE_NEXT
  >,
): string => {
  const { unit, amount, isInclusive } = filter as DateFilter<
    | DATE_RELATIONS.PREVIOUS
    | DATE_RELATIONS.NEXT
    | DATE_RELATIONS.IN_THE_LAST
    | DATE_RELATIONS.IN_THE_NEXT
  >;
  const ret: string =
    isNaN(Number(amount)) || amount === null
      ? `${unit}` //filter is DATE_RELATIONS.CURRENT
      : `${amount}${relationSplitChar}${unit}`; //filter is DATE_RELATIONS.PREVIOUS | DATE_RELATIONS.NEXT

  return isInclusive != null
    ? `${ret}${relationSplitChar}${isInclusive ? 'inclusive' : 'exclusive'}`
    : ret;
};

export type NameOn<name extends DateFilterName> =
  `${name}${typeof relationSplitChar}on`;
export type NameBefore<name extends DateFilterName> =
  `${name}${typeof relationSplitChar}before`;
export type NameAfter<name extends DateFilterName> =
  `${name}${typeof relationSplitChar}after`;
export type NameRange<name extends DateFilterName> =
  `${name}${typeof relationSplitChar}range`;
export type NameRelation<name extends DateFilterName> =
  `${name}${typeof relationSplitChar}relation`;

export const transFormDateFieldToFilter = <
  NAME extends DateFilterName,
  RELATION extends DATE_RELATIONS,
>(
  name: NAME,
  filter?: DateFilter<RELATION>,
): undefined | TransformedDateFilter<NAME, RELATION> => {
  if (!filter || !filter.relation) {
    return undefined;
  }

  const { relation } = filter;

  switch (relation) {
    case DATE_RELATIONS.ON:
      if (!filter.startDate) {
        return undefined;
      }
      return {
        [`${name}${relationSplitChar}${relation}`]: filter.startDate.format(),
      } as TransformedDateFilter<NAME, RELATION>;
    case DATE_RELATIONS.BEFORE:
    case DATE_RELATIONS.AFTER:
      if (!filter.startDate) {
        if (!filter.relativeDate) {
          return undefined;
        }
        return {
          [`${name}${relationSplitChar}${relation}`]: filter.relativeDate,
        } as TransformedDateFilter<NAME, RELATION>;
      }
      return {
        [`${name}${relationSplitChar}${relation}`]: filter.startDate.format(),
      } as TransformedDateFilter<NAME, RELATION>;
    case DATE_RELATIONS.BETWEEN:
      if (!filter.startDate || !filter.endDate) {
        return undefined;
      }
      return {
        [`${name}${relationSplitChar}range`]: `${filter.startDate.format()}${rangeSplitChar}${filter.endDate.format()}`,
      } as TransformedDateFilter<NAME, RELATION>;
    case DATE_RELATIONS.IN_THE_LAST:
    case DATE_RELATIONS.IN_THE_NEXT:
    case DATE_RELATIONS.IN_THIS_HOUR:
    case DATE_RELATIONS.TODAY:
    case DATE_RELATIONS.IN_THIS_WEEK:
    case DATE_RELATIONS.IN_THIS_MONTH:
    case DATE_RELATIONS.IN_THIS_YEAR:
    case DATE_RELATIONS.NEVER:
    case DATE_RELATIONS.PREVIOUS:
    case DATE_RELATIONS.CURRENT:
    case DATE_RELATIONS.NEXT:
      if (!filter.unit) {
        return undefined;
      }
      return {
        [`${name}${relationSplitChar}${relation}`]: formatRelativeDateFilter(
          filter as DateFilter<
            | DATE_RELATIONS.PREVIOUS
            | DATE_RELATIONS.CURRENT
            | DATE_RELATIONS.NEXT
            | DATE_RELATIONS.IN_THE_LAST
            | DATE_RELATIONS.IN_THE_NEXT
            | DATE_RELATIONS.IN_THIS_HOUR
            | DATE_RELATIONS.TODAY
            | DATE_RELATIONS.IN_THIS_WEEK
            | DATE_RELATIONS.IN_THIS_MONTH
            | DATE_RELATIONS.IN_THIS_YEAR
            | DATE_RELATIONS.NEVER
          >,
        ),
      } as TransformedDateFilter<NAME, RELATION>;
  }
};

export type NameDateFilter<NAME extends DateFilterName> = `${NAME}DateFilter`;

export type DateFilters = Partial<
  Record<NameDateFilter<DateFilterName>, DateFilter<DATE_RELATIONS>>
>;

export const transformRelativeDateToFilter = (
  dates: Partial<TransformedDateFilter<DateFilterName, DATE_RELATIONS>>,
): DateFilters => {
  const res: DateFilters = {};
  Object.keys(dates).forEach((_key) => {
    const key = _key as keyof typeof dates;
    const value = dates[key] as string; //the encoded string for key
    const splitedKey = (key || '').split(relationSplitChar);
    const name = splitedKey[0] as DateFilterName;
    const relation = splitedKey[1] as DATE_RELATIONS | 'range';
    let result: DateFilter<DATE_RELATIONS> | undefined;
    switch (relation) {
      case DATE_RELATIONS.ON:
      case DATE_RELATIONS.AFTER:
      case DATE_RELATIONS.BEFORE:
        result = { relation, startDate: moment(value) };
        break;
      case DATE_RELATIONS.BETWEEN:
      case 'range': {
        const splitedDate = value.split(rangeSplitChar);
        result = {
          relation: relation === 'range' ? DATE_RELATIONS.BETWEEN : relation,
          startDate: moment(splitedDate[0]),
          endDate: moment(splitedDate[1]),
        };
        break;
      }
      case DATE_RELATIONS.CURRENT:
        result = {
          relation,
          unit: value,
        };
        break;
      case DATE_RELATIONS.PREVIOUS:
      case DATE_RELATIONS.NEXT: {
        const values = value.split(relationSplitChar);
        result = {
          relation,
          amount: values[0],
          unit: values[1],
          ...(values.length > 2 && { isInclusive: values[2] === 'inclusive' }),
        };
        break;
      }
    }
    if (result) {
      res[`${name}DateFilter`] = result;
    }
  });
  return res;
};

export const newTransformRelativeDateToFilter = (
  key: string,
  value: string,
): DateFilter<DATE_RELATIONS> | undefined => {
  const underscoreIndex = key.indexOf(relationSplitChar);
  const relationValue = (key || '').slice(underscoreIndex + 1);
  const relation = relationValue as DATE_RELATIONS | 'range';
  let result: DateFilter<DATE_RELATIONS> | undefined;
  switch (relation) {
    case DATE_RELATIONS.ON:
      result = { relation, startDate: moment(value) };
      break;
    case DATE_RELATIONS.AFTER:
    case DATE_RELATIONS.BEFORE: {
      if (/\d/.test(value))
        result = { relation, startDate: moment(value), relativeDate: '' };
      else result = { relation, relativeDate: value };
      break;
    }
    case DATE_RELATIONS.BETWEEN:
    case 'range': {
      const splitedDate = value.split(rangeSplitChar);
      result = {
        relation: relation === 'range' ? DATE_RELATIONS.BETWEEN : relation,
        startDate: moment(splitedDate[0]),
        endDate: moment(splitedDate[1]),
      };
      break;
    }
    case DATE_RELATIONS.CURRENT:
      switch (value) {
        case TimeUnitSingular.HOUR:
          result = { relation: DATE_RELATIONS.IN_THIS_HOUR, unit: value };
          break;
        case TimeUnitSingular.DAY:
          result = { relation: DATE_RELATIONS.TODAY, unit: value };
          break;
        case TimeUnitSingular.WEEK:
          result = { relation: DATE_RELATIONS.IN_THIS_WEEK, unit: value };
          break;
        case TimeUnitSingular.MONTH:
          result = { relation: DATE_RELATIONS.IN_THIS_MONTH, unit: value };
          break;
        case TimeUnitSingular.YEAR:
          result = { relation: DATE_RELATIONS.IN_THIS_YEAR, unit: value };
          break;
        case TimeUnitSingular.NEVER:
          result = { relation: DATE_RELATIONS.NEVER, unit: value };
          break;
      }
      break;
    case DATE_RELATIONS.PREVIOUS: {
      const values = value.split(relationSplitChar);
      result = {
        relation: DATE_RELATIONS.IN_THE_LAST,
        amount: values[0],
        unit: values[1],
        ...(values.length > 2 && { isInclusive: values[2] === 'inclusive' }),
      };
      break;
    }
    case DATE_RELATIONS.NEXT: {
      const values = value.split(relationSplitChar);
      result = {
        relation: DATE_RELATIONS.IN_THE_NEXT,
        amount: values[0],
        unit: values[1],
        ...(values.length > 2 && { isInclusive: values[2] === 'inclusive' }),
      };
      break;
    }
  }
  return result;
};

export const transformMetadataToFilter = (
  metadata?: string,
): undefined | Record<string, string> => {
  if (!metadata) {
    return undefined;
  }
  const [key, value] = metadata.split('=');
  return {
    [key]: value,
  };
};

export const transformDateRangeToFilters = <NAME extends DateFilterName>(
  range: [moment.Moment, moment.Moment?],
  name: NAME,
) => {
  const [before, after] = range || [];
  if (before && after) {
    return {
      [`${name}${relationSplitChar}range`]: `${before.format()},${after.format()}`,
    } as Record<NameRange<NAME>, string>;
  }
  if (before) {
    return {
      [`${name}${relationSplitChar}on`]: before.format(),
    } as Record<NameOn<NAME>, string>;
  }
  return {};
};

export const transformFiltersToDates = (
  dates: TransformedDateFilter<DateFilterName, DATE_RELATIONS>,
): Record<string, DateFilter<DATE_RELATIONS>> => {
  const res: Record<string, DateFilter<DATE_RELATIONS>> = {};
  Object.keys(dates).forEach((_key) => {
    const key = _key as keyof typeof dates;
    const splitedKey = (key || '').split(relationSplitChar);
    const relation = splitedKey[1];
    let result: DateFilter<DATE_RELATIONS> | undefined;
    if (relation === DATE_RELATIONS.ON) {
      result = {
        relation,
        startDate: moment(dates[key]),
      };
    }
    if (relation === DATE_RELATIONS.BETWEEN || relation === 'range') {
      const splitedDate = dates[key].split(rangeSplitChar);
      result = {
        relation: relation === 'range' ? DATE_RELATIONS.BETWEEN : relation,
        startDate: moment(splitedDate[0]),
        endDate: moment(splitedDate[1]),
      };
    }
    if (result) {
      res[`${splitedKey[0]}Date`] = result;
    }
  });
  return res;
};

export const transformArrayToFilters = (
  filters: Record<string, string | string[]>,
): Record<string, string> => {
  const ret: Record<string, string> = {};
  for (const [key, val] of Object.entries(filters)) {
    if (Array.isArray(val)) {
      ret[`${key}${relationSplitChar}in`] = val.join();
    } else {
      ret[key] = val;
    }
  }

  return ret;
};
